import { useCan } from '@features/auth';
import { cn } from '@lib/cn';
import {
    Badge,
    Button,
    Card,
    Chip,
    EmptyState,
    ErrorState,
    FlagKeyPill,
    IconArrowRight,
    IconPlus,
    IconSearch,
    LoadingRows,
    PageHeader,
    PropagationNotice,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { defaultWriteFrom } from '../api/default-write';
import { useFlags, useSetDefault } from '../hooks/use-flags';
import {
    FLAG_TYPE_LABEL,
    FLAG_TYPE_ORDER,
    FLAG_TYPE_PARAM,
    flagTypeFromParam,
} from '../schemas/flag-type';
import { LIFECYCLE_LABEL, LIFECYCLE_ORDER, LIFECYCLE_TONE } from '../schemas/lifecycle';
import { PURPOSE_LABEL, PURPOSE_ORDER, PURPOSE_TONE } from '../schemas/purpose';

import { CreateFlagDialog } from './CreateFlagDialog';
import { flagFacets } from './flag-filter';
import { ReadOnlyNotice } from './ReadOnlyNotice';
import { offersRollout } from './rollout-ui';

import type { FlagLifecycle, FlagListItem, FlagPurpose, FlagType } from '../schemas/flag';

const NO_ROWS: readonly FlagListItem[] = [];

/**
 * Empty-tab copy. The two kinds have different readers, so "nothing here" means
 * a different thing on each tab.
 */
const EMPTY_TAB_DESCRIPTION: Record<FlagType, string> = {
    veevart2: 'No flag read by Veevart 2 services exists in this account yet.',
    salesforce: "No flag is projected into customers' Salesforce orgs yet.",
};

export function FlagsPage() {
    const navigate = useNavigate();
    const flags = useFlags();
    const [params, setParams] = useSearchParams();
    const [lifecycle, setLifecycle] = useState<FlagLifecycle | 'all'>('all');
    const [purpose, setPurpose] = useState<FlagPurpose | 'all'>('all');
    const [search, setSearch] = useState('');
    const [creating, setCreating] = useState(false);
    // Must match `@RequiresRole('engineer')` on `POST /api/flags` — a key is
    // permanent, so creation sits above the ops tier.
    const canCreate = useCan('engineer');

    // The tab lives in the URL (ADR 0009 §5), so a link to the Salesforce flags
    // opens the Salesforce flags. The two kinds never share one list: their
    // readers are disjoint, and a mixed table invites reading one as the other.
    const flagType = flagTypeFromParam(params.get(FLAG_TYPE_PARAM));
    const selectType = (next: FlagType) => {
        setParams(
            (current) => {
                const updated = new URLSearchParams(current);
                updated.set(FLAG_TYPE_PARAM, next);
                return updated;
            },
            { replace: true },
        );
    };

    // A `?? []` inline would mint a new array on every render and defeat both
    // memos below — they would recompute on every keystroke anywhere in the tree.
    const rows = flags.data ?? NO_ROWS;

    const typeCounts = useMemo(() => {
        const byType = new Map<FlagType, number>();
        for (const row of rows) {
            byType.set(row.definition.type, (byType.get(row.definition.type) ?? 0) + 1);
        }
        return byType;
    }, [rows]);

    const ofType = useMemo(
        () => rows.filter((row) => row.definition.type === flagType),
        [rows, flagType],
    );

    /**
     * Counts come from the loaded rows rather than a separate endpoint. The
     * mock hard-coded them ("All 32", "GA 21") against a seven-row sample, so
     * the chips disagreed with the table underneath them. Deriving guarantees
     * they cannot — and once a second chip row existed, keeping that guarantee
     * stopped being a one-liner. See `flag-filter.ts` for the rule the two rows
     * count by, and `flag-filter.test.ts` for the cases that pin it.
     */
    const { visible, lifecycleCounts, purposeCounts } = useMemo(
        () => flagFacets(rows, { type: flagType, lifecycle, purpose, search }),
        [rows, flagType, lifecycle, purpose, search],
    );

    const isSalesforce = flagType === 'salesforce';

    const createButton = (label: string) => (
        <Button
            disabled={!canCreate}
            onClick={() => {
                setCreating(true);
            }}
        >
            <IconPlus className="size-4" />
            {label}
        </Button>
    );

    return (
        <>
            <PageHeader
                eyebrow="Configuration"
                title="Feature Flags"
                description="Flags are created here at runtime and stored in DynamoDB — there is no code catalog. Veevart 2 flags are read by Veevart 2 services; Salesforce flags are projected into customers' orgs. Every change is audited against the operator who made it."
                actions={createButton('New flag')}
            />

            <ReadOnlyNotice />

            <Card>
                <div
                    role="tablist"
                    aria-label="Flag type"
                    className="flex gap-1 border-b border-line-subtle px-5"
                >
                    {FLAG_TYPE_ORDER.map((type) => (
                        <button
                            key={type}
                            type="button"
                            role="tab"
                            aria-selected={type === flagType}
                            onClick={() => {
                                selectType(type);
                            }}
                            className={cn(
                                '-mb-px border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors',
                                type === flagType
                                    ? 'border-brand text-accent-text'
                                    : 'border-transparent text-content-muted hover:text-content-strong',
                            )}
                        >
                            {FLAG_TYPE_LABEL[type]}
                            <b className="ml-1.5">{typeCounts.get(type) ?? 0}</b>
                        </button>
                    ))}
                </div>

                {/* Two chip rows, each labelled. Unlabelled they are
                    indistinguishable, and the axes answer different questions:
                    one is what a flag is for, the other where it is in its
                    life. Counting rule in `flag-filter.ts`. */}
                <div className="flex flex-wrap items-center gap-2.5 border-b border-line-subtle px-5 py-3">
                    <span className="w-16 text-[11px] uppercase tracking-wide text-content-muted">
                        Purpose
                    </span>
                    <Chip
                        active={purpose === 'all'}
                        onClick={() => {
                            setPurpose('all');
                        }}
                    >
                        All<b className="ml-0.5">{purposeCounts.get('all') ?? 0}</b>
                    </Chip>
                    {PURPOSE_ORDER.map((value) => (
                        <Chip
                            key={value}
                            active={purpose === value}
                            onClick={() => {
                                setPurpose(value);
                            }}
                        >
                            {PURPOSE_LABEL[value]}
                            <b className="ml-0.5">{purposeCounts.get(value) ?? 0}</b>
                        </Chip>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-2.5 border-b border-line-subtle px-5 py-3">
                    <span className="w-16 text-[11px] uppercase tracking-wide text-content-muted">
                        Lifecycle
                    </span>
                    <Chip
                        active={lifecycle === 'all'}
                        onClick={() => {
                            setLifecycle('all');
                        }}
                    >
                        All<b className="ml-0.5">{lifecycleCounts.get('all') ?? 0}</b>
                    </Chip>
                    {LIFECYCLE_ORDER.map((value) => (
                        <Chip
                            key={value}
                            active={lifecycle === value}
                            onClick={() => {
                                setLifecycle(value);
                            }}
                        >
                            {LIFECYCLE_LABEL[value]}
                            <b className="ml-0.5">{lifecycleCounts.get(value) ?? 0}</b>
                        </Chip>
                    ))}
                    <label className="ml-auto flex min-w-56 items-center gap-2 rounded-lg bg-surface-inset px-3 py-1.5 text-content-muted">
                        <IconSearch className="size-4" />
                        <span className="sr-only">Filter flags by key or name</span>
                        <input
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                            className="w-full bg-transparent text-[12.5px] outline-none placeholder:text-content-muted"
                            placeholder="Filter by key or name…"
                        />
                    </label>
                </div>

                {flags.isPending ? (
                    <LoadingRows label="Loading flags…" />
                ) : flags.isError ? (
                    <ErrorState error={flags.error} onRetry={() => void flags.refetch()} />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="No flags yet"
                        description="Nothing has been created in this account's table. A flag referenced in backend code does not appear here until someone creates its row."
                        action={createButton('Create the first flag')}
                    />
                ) : ofType.length === 0 ? (
                    <EmptyState
                        title={`No ${FLAG_TYPE_LABEL[flagType]} flags yet`}
                        description={EMPTY_TAB_DESCRIPTION[flagType]}
                        action={createButton('New flag')}
                    />
                ) : visible.length === 0 ? (
                    <EmptyState
                        title="Nothing matches those filters"
                        description="Try a different purpose or lifecycle, or clear the search box."
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-surface-sunken text-left text-[11px] uppercase tracking-wide text-content-muted">
                                    <th className="px-5 py-3 font-semibold">Flag</th>
                                    <th className="px-5 py-3 font-semibold">Category</th>
                                    <th className="px-5 py-3 font-semibold">Purpose</th>
                                    <th className="px-5 py-3 font-semibold">Lifecycle</th>
                                    <th className="px-5 py-3 font-semibold">Default</th>
                                    {/* A Salesforce flag has no rollout (D6); where its
                                        value lands is the column worth having instead. */}
                                    {isSalesforce ? (
                                        <th className="px-5 py-3 font-semibold">
                                            Salesforce field
                                        </th>
                                    ) : (
                                        offersRollout(flagType) && (
                                            <th className="px-5 py-3 font-semibold">Rollout</th>
                                        )
                                    )}
                                    <th className="px-5 py-3 font-semibold">Owner</th>
                                    <th className="px-5 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((row) => (
                                    <FlagRow key={row.definition.key} row={row} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <PropagationNotice className="mt-4 text-center text-xs text-content-faint" />

            <CreateFlagDialog
                open={creating}
                onClose={() => {
                    setCreating(false);
                }}
                onCreated={(key) => {
                    setCreating(false);
                    // Straight to the flag that was just created: it exists but
                    // is off and has no overrides, so the detail view is where
                    // the next thing an operator wants to do actually lives.
                    void navigate(`/flags/${encodeURIComponent(key)}`);
                }}
            />
        </>
    );
}

/**
 * One row, owning its own default-toggle mutation.
 *
 * Per-row rather than one mutation hoisted to the page, so an in-flight write on
 * one flag disables only that flag's switch. A shared mutation would grey out
 * every toggle in the table on any click, and — worse — a failure would attach
 * its error to whichever row rendered it last.
 */
function FlagRow({ row }: { row: FlagListItem }) {
    const { definition, default: current } = row;
    const setDefault = useSetDefault(definition.key);
    // Matches `@RequiresRole('engineer')` on `PUT /api/flags/:key/default` —
    // the platform default is every museum without an override.
    const canSetDefault = useCan('engineer');
    const isSalesforce = definition.type === 'salesforce';
    const showRollout = offersRollout(definition.type);
    // Flag, Category, Purpose, Lifecycle, Default, Owner, spacer — plus the
    // Salesforce-field or Rollout column when the type has one. Feeds the error
    // row's `colSpan` below; wrong here it under- or over-spans in silence.
    const columns = isSalesforce || showRollout ? 8 : 7;

    return (
        <>
            <tr className="border-t border-line-subtle text-[13.5px] hover:bg-surface-sunken">
                <td className="px-5 py-3">
                    <Link
                        to={`/flags/${encodeURIComponent(definition.key)}`}
                        className="inline-block"
                    >
                        <FlagKeyPill>{definition.key}</FlagKeyPill>
                    </Link>
                    <div className="mt-0.5 text-xs text-content-muted">{definition.name}</div>
                </td>
                <td className="px-5 py-3 text-content-body">{definition.category}</td>
                <td className="px-5 py-3">
                    <Badge tone={PURPOSE_TONE[definition.purpose]}>
                        {PURPOSE_LABEL[definition.purpose]}
                    </Badge>
                </td>
                <td className="px-5 py-3">
                    <Badge tone={LIFECYCLE_TONE[definition.lifecycle]} dot>
                        {LIFECYCLE_LABEL[definition.lifecycle]}
                    </Badge>
                </td>
                <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                        <Toggle
                            on={current.enabled}
                            busy={setDefault.isPending}
                            // An archived flag resolves `false` everywhere no
                            // matter what this says, so letting it be toggled
                            // would show a change that changes nothing.
                            disabled={current.archived}
                            // Omitting `onChange` is `Toggle`'s read-only mode:
                            // it renders inert rather than accepting a click
                            // the API would refuse.
                            onChange={
                                canSetDefault
                                    ? (next) => {
                                          setDefault.mutate(
                                              defaultWriteFrom(current, { enabled: next }),
                                          );
                                      }
                                    : undefined
                            }
                            aria-label={`Platform default for ${definition.key}`}
                        />
                        {current.archived && <Badge tone="neutral">Archived</Badge>}
                    </div>
                </td>
                {isSalesforce ? (
                    <td className="px-5 py-3 text-content-body">
                        <span className="font-mono text-xs">{definition.salesforceField}</span>
                    </td>
                ) : (
                    showRollout && (
                        <td className="px-5 py-3 text-content-body">
                            {current.rolloutPercentage === undefined ? (
                                <span className="text-xs text-content-faint">No gate</span>
                            ) : (
                                <span className="font-semibold">{current.rolloutPercentage}%</span>
                            )}
                        </td>
                    )
                )}
                <td className="px-5 py-3 text-content-body">{definition.owner}</td>
                <td className="px-5 py-3 text-right">
                    <Link
                        to={`/flags/${encodeURIComponent(definition.key)}`}
                        aria-label={`Open ${definition.key}`}
                        className="text-content-faint hover:text-accent-icon"
                    >
                        <IconArrowRight className="inline size-4" />
                    </Link>
                </td>
            </tr>
            {setDefault.error && (
                <tr className="border-t border-line-subtle">
                    <td colSpan={columns} className="px-5 py-2.5">
                        <WriteError error={setDefault.error} />
                    </td>
                </tr>
            )}
        </>
    );
}
